import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../../../app/Layout';
import HeaderContent from '../../General/HeaderContent';
import Breadcrumb from '../../General/Breadcrumb';
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading';
import {
  obtenerReporte,
  guardarReporte,
  enviarReporte,
  agregarAccion,
  eliminarAccion,
} from '../../../../actions/tutoriaHU9Actions';

const SectionBox = ({ title, number, children }) => (
  <div className="box mb-3">
    <div className="box-header with-border">
      <h5 className="mb-0">
        <span className="badge badge-primary mr-2">{number}</span>
        {title}
      </h5>
    </div>
    <div className="box-body">{children}</div>
  </div>
);

const CheckItem = ({ label, name, value, onChange, disabled }) => (
  <div className="form-check form-check-inline mr-4">
    <input
      className="form-check-input"
      type="checkbox"
      id={name}
      name={name}
      checked={value === 1 || value === '1' || value === true}
      onChange={e => onChange(name, e.target.checked ? 1 : 0)}
      disabled={disabled}
    />
    <label className="form-check-label" htmlFor={name}>{label}</label>
  </div>
);

const RadioGroup = ({ name, options, value, onChange, disabled }) => (
  <div className="d-flex flex-wrap" style={{ gap: 16 }}>
    {options.map(opt => (
      <div key={opt.value} className="form-check form-check-inline">
        <input
          className="form-check-input"
          type="radio"
          id={`${name}_${opt.value}`}
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(name, opt.value)}
          disabled={disabled}
        />
        <label className="form-check-label" htmlFor={`${name}_${opt.value}`}>{opt.label}</label>
      </div>
    ))}
  </div>
);

const ReporteSesion = () => {
  const { idSesion } = useParams();
  const dispatch = useDispatch();
  const { usuario } = useSelector(state => state.usuario);
  const { reporte, acciones, infoSesion, loadingReporte } = useSelector(state => state.tutoriaHU9);

  const [form, setForm]     = useState({});
  const [nuevaAccion, setNuevaAccion] = useState({ accion: '', responsable: '', fecha_limite: '' });

  useEffect(() => {
    if (usuario.id_usuario) {
      dispatch(obtenerReporte(idSesion, usuario.id_usuario));
    }
  }, [idSesion, usuario.id_usuario]);

  useEffect(() => {
    if (reporte) {
      setForm({
        ...reporte,
        horario: reporte.horario || infoSesion?.horario_proyecto || '',
      });
    }
  }, [reporte, infoSesion]);

  const bloqueado = reporte?.bloqueado == 1;

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleGuardar = () => {
    if (!reporte) return;
    dispatch(guardarReporte(reporte.id_reporte, form, false));
  };

  const handleEnviar = () => {
    if (!reporte) return;
    // Guardar primero y luego enviar
    dispatch(guardarReporte(reporte.id_reporte, form)).then(() => {
      dispatch(enviarReporte(reporte.id_reporte));
    });
  };

  const handleAgregarAccion = () => {
    if (!nuevaAccion.accion.trim() || !reporte) return;
    dispatch(agregarAccion(reporte.id_reporte, nuevaAccion));
    setNuevaAccion({ accion: '', responsable: '', fecha_limite: '' });
  };

  if (loadingReporte) {
    return <Layout><SpinnerLoading loading={true} /></Layout>;
  }

  return (
    <Layout>
      <HeaderContent title="Reporte de Sesión" icon="file-alt" />
      <Breadcrumb link={[
        { url: '/inicio',        nombre: 'Inicio',    icon: 'fas fa-home' },
        { url: '/tutor/eventos', nombre: 'Eventos' },
        { url: '',               nombre: 'Reporte de Sesión' },
      ]} />

      {/* Estado del reporte */}
      <div className="box p-3 mb-3 d-flex justify-content-between align-items-center">
        <div>
          <strong>Sesión #{idSesion}</strong>
          {reporte && (
            <Badge
              variant={reporte.estado === 'enviado' ? 'success' : 'warning'}
              className="ml-2"
            >
              {reporte.estado === 'enviado' ? 'Enviado' : 'Borrador'}
            </Badge>
          )}
          {reporte?.fecha_envio && (
            <small className="text-muted ml-2">Enviado: {reporte.fecha_envio}</small>
          )}
        </div>
        {!bloqueado && (
          <div>
            <Button variant="outline-primary" size="sm" className="mr-2" onClick={handleGuardar}>
              <FontAwesomeIcon icon={faSave} className="mr-1" />
              Guardar borrador
            </Button>
            <Button variant="success" size="sm" onClick={handleEnviar}>
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
              Enviar reporte
            </Button>
          </div>
        )}
        {bloqueado && (
          <Badge variant="success" className="p-2">Reporte enviado y bloqueado</Badge>
        )}
      </div>

      {/* Sección 1: Información de la sesión */}
      <SectionBox title="Información de la sesión" number="1">
        <div className="row">
          {[
            { label: 'Programa',        value: infoSesion.nombre_proyecto },
            { label: 'Módulo',          value: infoSesion.nombre_modulo   },
            { label: 'Tema de la clase',value: infoSesion.nombre_sesion   },
            { label: 'Docente',         value: infoSesion.nombre_docente  },
            { label: 'Fecha',           value: infoSesion.fecha_sesion    },
            { label: 'Tutor',           value: infoSesion.nombre_tutor    },
          ].map(campo => (
            <div key={campo.label} className="col-md-4 mb-2">
              <small className="text-muted d-block">{campo.label}</small>
              <strong>{campo.value || '—'}</strong>
            </div>
          ))}
          <div className="col-md-4 mb-2">
            <small className="text-muted d-block">Horario</small>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="ej. 7:00 PM - 10:00 PM"
              value={form.horario || ''}
              onChange={e => handleChange('horario', e.target.value)}
              disabled={bloqueado}
            />
          </div>
        </div>
      </SectionBox>

      {/* Sección 2: Asistencia */}
      <SectionBox title="Asistencia" number="2">
        <div className="row">
          {[
            { name: 'alumnos_inscritos',    label: 'Inscritos' },
            { name: 'alumnos_conectados',   label: 'Conectados' },
            { name: 'pico_maximo',          label: 'Pico máximo' },
            { name: 'permanecieron_final',  label: 'Hasta el final' },
          ].map(f => (
            <div key={f.name} className="col-md-3">
              <div className="form-group">
                <label>{f.label}</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={form[f.name] || 0}
                  onChange={e => handleChange(f.name, e.target.value)}
                  disabled={bloqueado}
                  min={0}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="form-group">
          <label>Observaciones de asistencia</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={form.obs_asistencia || ''}
            onChange={e => handleChange('obs_asistencia', e.target.value)}
            disabled={bloqueado}
          />
        </div>
      </SectionBox>

      {/* Sección 3: Inicio de sesión */}
      <SectionBox title="Inicio de sesión" number="3">
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <CheckItem label="Sala abierta a tiempo (6:50 PM)" name="sala_abierta_tiempo"  value={form.sala_abierta_tiempo}  onChange={handleChange} disabled={bloqueado} />
          <CheckItem label="Docente puntual"                  name="docente_puntual"       value={form.docente_puntual}       onChange={handleChange} disabled={bloqueado} />
          <CheckItem label="Clase inició a tiempo (7:00 PM)"  name="clase_inicio_puntual"  value={form.clase_inicio_puntual}  onChange={handleChange} disabled={bloqueado} />
          <CheckItem label="Recursos listos en plataforma"    name="recursos_listos"       value={form.recursos_listos}       onChange={handleChange} disabled={bloqueado} />
          <CheckItem label="Mensajes del chat respondidos al 100%" name="chat_respondido"  value={form.chat_respondido}       onChange={handleChange} disabled={bloqueado} />
          <CheckItem label="Grabación subida a Vimeo" name="grabacion_vimeo" value={form.grabacion_vimeo}   onChange={handleChange} disabled={bloqueado} />
        </div>
      </SectionBox>

      {/* Sección 4: Incidencias */}
      <SectionBox title="Incidencias" number="4">
        {[
          { key: 'tecnica',   label: 'Técnica'          },
          { key: 'academica', label: 'Académica'         },
          { key: 'particip',  label: 'Participación'     },
          { key: 'otra',      label: 'Otra'              },
        ].map(inc => (
          <div key={inc.key} className="row mb-2">
            <div className="col-md-2 d-flex align-items-center">
              <strong>{inc.label}</strong>
            </div>
            <div className="col-md-5">
              <textarea
                className="form-control form-control-sm"
                placeholder="Descripción"
                rows={2}
                value={form[`incidencia_${inc.key}_desc`] || ''}
                onChange={e => handleChange(`incidencia_${inc.key}_desc`, e.target.value)}
                disabled={bloqueado}
              />
            </div>
            <div className="col-md-5">
              <textarea
                className="form-control form-control-sm"
                placeholder="Resolución"
                rows={2}
                value={form[`incidencia_${inc.key}_resol`] || ''}
                onChange={e => handleChange(`incidencia_${inc.key}_resol`, e.target.value)}
                disabled={bloqueado}
              />
            </div>
          </div>
        ))}
      </SectionBox>

      {/* Sección 5: Preguntas frecuentes */}
      <SectionBox title="Preguntas frecuentes del chat" number="5">
        <textarea
          className="form-control form-control-sm"
          rows={3}
          placeholder="Preguntas destacadas que hicieron los alumnos..."
          value={form.preguntas_frecuentes || ''}
          onChange={e => handleChange('preguntas_frecuentes', e.target.value)}
          disabled={bloqueado}
        />
      </SectionBox>

      {/* Sección 6: Participación */}
      <SectionBox title="Participación" number="6">
        <div className="form-group">
          <label>Nivel de participación</label>
          <RadioGroup
            name="nivel_participacion"
            options={[{ value: 'alta', label: 'Alta' }, { value: 'media', label: 'Media' }, { value: 'baja', label: 'Baja' }]}
            value={form.nivel_participacion || ''}
            onChange={handleChange}
            disabled={bloqueado}
          />
        </div>
        <div className="form-group">
          <label>Observaciones</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={form.obs_participacion || ''}
            onChange={e => handleChange('obs_participacion', e.target.value)}
            disabled={bloqueado}
          />
        </div>
      </SectionBox>

      {/* Sección 7: Desempeño docente */}
      <SectionBox title="Desempeño del docente" number="7">
        {[
          { name: 'claridad_explicacion', label: 'Claridad de explicación' },
          { name: 'manejo_tiempo',        label: 'Manejo del tiempo'       },
          { name: 'interaccion_alumnos',  label: 'Interacción con alumnos' },
        ].map(f => (
          <div key={f.name} className="form-group">
            <label>{f.label}</label>
            <RadioGroup
              name={f.name}
              options={[{ value: 'bueno', label: 'Bueno' }, { value: 'regular', label: 'Regular' }]}
              value={form[f.name] || ''}
              onChange={handleChange}
              disabled={bloqueado}
            />
          </div>
        ))}
        <div className="form-group">
          <label>Observaciones del desempeño</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={form.obs_desempeno || ''}
            onChange={e => handleChange('obs_desempeno', e.target.value)}
            disabled={bloqueado}
          />
        </div>
      </SectionBox>

      {/* Sección 8: Quejas */}
      <SectionBox title="Comentarios y quejas" number="8">
        <textarea
          className="form-control form-control-sm"
          rows={3}
          placeholder="Quejas o comentarios relevantes de los alumnos..."
          value={form.comentarios_quejas || ''}
          onChange={e => handleChange('comentarios_quejas', e.target.value)}
          disabled={bloqueado}
        />
      </SectionBox>

      {/* Sección 9: Evaluación general */}
      <SectionBox title="Evaluación general de la sesión" number="9">
        <div className="form-group">
          <RadioGroup
            name="evaluacion_general"
            options={[
              { value: 'excelente', label: 'Excelente' },
              { value: 'buena',     label: 'Buena'     },
              { value: 'regular',   label: 'Regular'   },
              { value: 'deficiente',label: 'Deficiente'},
            ]}
            value={form.evaluacion_general || ''}
            onChange={handleChange}
            disabled={bloqueado}
          />
        </div>
        <div className="form-group">
          <label>Motivo / justificación</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={form.motivo_evaluacion || ''}
            onChange={e => handleChange('motivo_evaluacion', e.target.value)}
            disabled={bloqueado}
          />
        </div>
      </SectionBox>

      {/* Sección 10: Acciones a seguir */}
      <SectionBox title="Acciones a seguir" number="10">
        {acciones.length > 0 && (
          <table className="table table-sm table-bordered mb-3">
            <thead className="thead-light">
              <tr>
                <th>Acción</th>
                <th style={{ width: 160 }}>Responsable</th>
                <th style={{ width: 130 }}>Fecha límite</th>
                {!bloqueado && <th style={{ width: 50 }}></th>}
              </tr>
            </thead>
            <tbody>
              {acciones.map(acc => (
                <tr key={acc.id_accion}>
                  <td>{acc.accion}</td>
                  <td>{acc.responsable || '—'}</td>
                  <td>{acc.fecha_limite || '—'}</td>
                  {!bloqueado && (
                    <td className="text-center">
                      <button
                        className="btn btn-danger btn-xs"
                        onClick={() => dispatch(eliminarAccion(acc.id_accion))}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!bloqueado && (
          <div className="row align-items-end">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Acción a seguir *"
                value={nuevaAccion.accion}
                onChange={e => setNuevaAccion(p => ({ ...p, accion: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Responsable"
                value={nuevaAccion.responsable}
                onChange={e => setNuevaAccion(p => ({ ...p, responsable: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                value={nuevaAccion.fecha_limite}
                onChange={e => setNuevaAccion(p => ({ ...p, fecha_limite: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <Button variant="primary" size="sm" onClick={handleAgregarAccion}>
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Agregar
              </Button>
            </div>
          </div>
        )}
      </SectionBox>

      {/* Sección 11: Observaciones finales */}
      <SectionBox title="Observaciones finales" number="11">
        <textarea
          className="form-control form-control-sm"
          rows={3}
          placeholder="Observaciones generales del tutor sobre la sesión..."
          value={form.observaciones_finales || ''}
          onChange={e => handleChange('observaciones_finales', e.target.value)}
          disabled={bloqueado}
        />
      </SectionBox>

      {/* Botones finales */}
      {!bloqueado && (
        <div className="box p-3 d-flex justify-content-between align-items-center mb-4">
          <Link to="/tutor/eventos" className="btn btn-secondary">
            Volver
          </Link>
          <div>
            <Button variant="outline-primary" className="mr-2" onClick={handleGuardar}>
              <FontAwesomeIcon icon={faSave} className="mr-1" />
              Guardar borrador
            </Button>
            <Button variant="success" onClick={handleEnviar}>
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
              Enviar reporte
            </Button>
          </div>
        </div>
      )}
      {bloqueado && (
        <div className="mb-4">
          <Link to="/tutor/eventos" className="btn btn-secondary">
            Volver a Eventos
          </Link>
        </div>
      )}
    </Layout>
  );
};

export default ReporteSesion;
